import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import "./Login.scss";

const API_URL = process.env.REACT_APP_AUTH_URL || "https://authz-api.cms.chargingadda.com";

// Helper to get subdomain properly
const getSubdomain = (hostname: string): string => {
  const parts = hostname.split('.');
  if (parts.length > 3 || (parts.length === 3 && parts[1] === 'business')) {
    return parts[0];
  }
  return "";
};

const Callback: React.FC = () => {
  const navigate = useNavigate();
  const { setUser } = useUser();

  useEffect(() => {
    const handleCallback = async () => {
      // Check if already logged in
      const existingToken = localStorage.getItem("access_token");
      
      if (existingToken) {
        const redirectTo = sessionStorage.getItem("post_login_redirect") || "/dashboard";
        sessionStorage.removeItem("post_login_redirect");
        console.log("Already logged in, redirecting to", redirectTo);
        navigate(redirectTo, { replace: true });
        return;
      }

      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const error = params.get("error");
        
        if (error) {
          console.error("❌ Auth error from Zitadel:", error);
          alert(`Authentication failed: ${error}`);
          navigate("/login", { replace: true });
          return;
        }

        if (!code) {
          console.error("❌ No authorization code found");
          navigate("/login", { replace: true });
          return;
        }

        console.log("🔍 Callback received with code");

        // Check current location
        const currentHostname = window.location.hostname;
        const currentSubdomain = getSubdomain(currentHostname);
        
        console.log("📍 Callback on hostname:", currentHostname);
        console.log("📍 Detected subdomain:", currentSubdomain || "(main domain)");
        
        const isMainDomain = !currentSubdomain || currentHostname === "business.chargingadda.com";
        
        if (isMainDomain) {
          // On main domain - redirect to original subdomain
          console.log("🔄 On main domain, redirecting to subdomain...");
          
          let originalSubdomain = localStorage.getItem("original_subdomain");
          
          // Fallback to cookie if localStorage cleared
          if (!originalSubdomain) {
            const cookies = document.cookie.split(';');
            for (let cookie of cookies) {
              const [name, value] = cookie.trim().split('=');
              if (name === 'org_subdomain') {
                originalSubdomain = value;
                localStorage.setItem("original_subdomain", originalSubdomain);
                break;
              }
            }
          }
          
          if (!originalSubdomain) {
            console.error("❌ Original subdomain not found!");
            alert("Session lost. Please try logging in again.");
            navigate("/login", { replace: true });
            return;
          }
          
          const redirectUrl = `https://${originalSubdomain}.business.chargingadda.com/callback?code=${code}`;
          console.log("🔄 Redirecting to:", redirectUrl);
          window.location.replace(redirectUrl);
          return;
        }

        // On subdomain - proceed with token exchange
        console.log("✅ On subdomain, exchanging token...");
        
        const verifier = localStorage.getItem("code_verifier");
        
        if (!verifier) {
          console.error("❌ Code verifier not found!");
          alert("Session lost. Please try logging in again.");
          navigate("/login", { replace: true });
          return;
        }

        // Fetch config
        const configRes = await fetch(`${API_URL}/auth/config`, {
          credentials: 'include'
        });
        
        if (!configRes.ok) throw new Error("Failed to load config");
        const config = await configRes.json();

        console.log("🔄 Exchanging code for token...");
        
        // Exchange code for tokens
        const tokenRes = await fetch(`${config.authority}/oauth/v2/token`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            grant_type: "authorization_code",
            code,
            redirect_uri: config.redirect_uri,
            client_id: config.client_id,
            code_verifier: verifier,
          }),
        });

        if (!tokenRes.ok) {
          const errorText = await tokenRes.text();
          console.error("❌ Token exchange failed:", errorText);
          throw new Error(`Token exchange failed: ${errorText}`);
        }
        
        const tokens = await tokenRes.json();
        console.log("✅ Tokens received");

        // Store tokens
        localStorage.setItem("access_token", tokens.access_token);
        localStorage.setItem("id_token", tokens.id_token);

        console.log("✅ Fetching user info...");
        
        // Fetch user info
        const userRes = await fetch(`${API_URL}/auth/userinfo`, {
          headers: {
            Authorization: `Bearer ${tokens.access_token}`,
          },
          credentials: 'include'
        });

        if (!userRes.ok) {
          const errorText = await userRes.text();
          throw new Error(`Failed to fetch user info: ${errorText}`);
        }

        const userInfo = await userRes.json();
        console.log("👤 User info loaded:", userInfo);

        // Store user info and current org (for this session)
        localStorage.setItem("user_info", JSON.stringify(userInfo));
        
        // ✅ Store current org for THIS SESSION only
        // This helps during refresh, but allows switching orgs in new tabs
        localStorage.setItem("current_org_subdomain", currentSubdomain);
        localStorage.setItem("current_org_id", config.org_id || "");
        
        console.log("💾 Stored current org for session:", currentSubdomain);
        
        // Set user in context
        setUser(userInfo);
        
        // Clean up temporary auth data
        localStorage.removeItem("code_verifier");
        localStorage.removeItem("original_subdomain");
        
        const redirectTo = sessionStorage.getItem("post_login_redirect") || "/dashboard";
        sessionStorage.removeItem("post_login_redirect");
        console.log("✅ Login complete! Redirecting to", redirectTo);

        setTimeout(() => {
          navigate(redirectTo, { replace: true });
        }, 100);

      } catch (err:any) {
        console.error("❌ Callback error:", err);
        alert(`Login failed: ${err.message || err}`);
        
        localStorage.removeItem("code_verifier");
        localStorage.removeItem("original_subdomain");
        
        navigate("/login", { replace: true });
      }
    };

    handleCallback();
  }, [navigate, setUser]);

  return (
    <div className="login-container">
      <div className="loader"></div>
    </div>
  );
};

export default Callback;
